import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Skeleton } from '../ui/skeleton';
import { ComplianceTrend } from './ComplianceTrend';
import { formatDateTime } from './eeocUtils';

const ModuleStatusGrid = ({ modules }) => (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4" data-testid="eeoc-history-modules">
        {modules.map((m) => (
            <div key={m.id} className="rounded-lg border px-3 py-2">
                <p className="truncate text-xs font-medium">{m.title}</p>
                <p className={`text-xs ${m.status === 'completed' ? 'text-emerald-600' : m.status === 'attempted' ? 'text-amber-600' : 'text-muted-foreground'}`}>
                    {m.status === 'completed' ? 'Passed' : m.status === 'attempted' ? 'Not passed yet' : 'Not started'}
                    {m.best_score !== null && m.best_score !== undefined && ` · best ${m.best_score}`}
                </p>
            </div>
        ))}
    </div>
);

const SessionsTable = ({ points, onOpen }) => (
    <div className="space-y-2" data-testid="eeoc-history-sessions">
        {[...points].reverse().map((p) => (
            <div key={p.session_id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{p.module_title}</p>
                    <p className="text-xs text-muted-foreground">{formatDateTime(p.date)} · pass mark {p.passing_score}</p>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold tabular-nums">{p.score}</span>
                    <Badge className={p.passed ? 'bg-emerald-500 hover:bg-emerald-500' : 'bg-red-500 hover:bg-red-500'}>{p.passed ? 'PASS' : 'FAIL'}</Badge>
                    <Button size="sm" variant="ghost" onClick={() => onOpen(p.session_id)} data-testid={`eeoc-history-open-${p.session_id}`}>
                        <ExternalLink className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        ))}
    </div>
);

export const EEOCHistoryDialog = ({ api, userId, open, onOpenChange }) => {
    const navigate = useNavigate();
    const [history, setHistory] = useState(null);

    useEffect(() => {
        if (!open || !userId) return;
        let active = true;
        setHistory(null);
        api.get(`/eeoc-training/admin/users/${userId}/history`)
            .then(({ data }) => active && setHistory(data))
            .catch(() => active && setHistory({ error: true }));
        return () => { active = false; };
    }, [api, userId, open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[88vh] flex flex-col" data-testid="eeoc-history-dialog">
                <DialogHeader>
                    <DialogTitle>EEOC compliance history{history?.user ? ` · ${history.user.name}` : ''}</DialogTitle>
                    <DialogDescription>
                        {history?.user ? `${history.user.email}${history.user.department ? ` · ${history.user.department}` : ''} · current pass mark ${history.passing_score}` : 'Loading...'}
                    </DialogDescription>
                </DialogHeader>
                <ScrollArea className="flex-1 pr-3">
                    {!history ? (
                        <div className="space-y-3"><Skeleton className="h-48" /><Skeleton className="h-32" /></div>
                    ) : history.error ? (
                        <p className="text-sm text-red-600">Could not load this recruiter's history.</p>
                    ) : (
                        <div className="space-y-6 pb-2">
                            <ComplianceTrend points={history.points} summary={history.summary} passingScore={history.passing_score} testId="eeoc-history-trend" />
                            <ModuleStatusGrid modules={history.modules} />
                            {history.points.length > 0 && <SessionsTable points={history.points} onOpen={(id) => navigate(`/eeoc-training/session/${id}`)} />}
                        </div>
                    )}
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
};
